import { Flag, Footprints, Hand, Link, Play, Unlink, type LucideIcon } from 'lucide-react';
import type {
  Board,
  BoardHand,
  BoardHoldRole,
  CircuitHighlightColor,
} from '../../../types/device';
import type { BoardHold } from '../../../types/climb';
import { holdKey } from './geometry';
import {
  assignCircuitHands,
  circuitFeet,
  circuitHandsOf,
  circuitNumbers,
  circuitStartingHand,
  circuitSteps,
  clearCircuitHands,
  orderCircuit,
  otherCircuitHand,
  swapCircuitHand,
} from '../circuit';

/**
 * Legend order, left to right; the selector starts on the first one.
 *
 * `match` — a hold both hands come onto — stands where `no_match` used to. A
 * circuit used to say a match by using the same hold twice in a row, which is
 * what a match is: one move per hand. It no longer does. One hold is one entry,
 * and the role says how many moves it is worth (see circuit.ts), because a run
 * that could visit a hold twice could not be read off the wall: one LED, one
 * circle, two numbers, and nothing to say which tap meant which.
 *
 * `no_match` is still read and drawn — problems were painted with it — but is not
 * offered: in a circuit every other hold is already one move of one hand.
 */
export const ROLE_CYCLE: BoardHoldRole[] = ['start', 'hand', 'foot', 'match', 'finish'];

/**
 * What the legend arms on an empty board — and what it goes back to for the next
 * problem. A problem is painted from its start holds, so that is what the first
 * tap should be.
 *
 * A different question from DEFAULT_ROLE below, which is where the legend lands
 * when the colour in hand runs out of slots: that one is mid-problem, and by
 * then the starts are already on the wall.
 */
export const INITIAL_ROLE: BoardHoldRole = ROLE_CYCLE[0];

/** What the legend falls back to once the selected colour has no slot left. */
export const DEFAULT_ROLE: BoardHoldRole = 'hand';

/**
 * A hold both hands come onto — one hold, two of the circuit's moves. The other
 * half of what a second press on the hold in hand steps between; see
 * useBoardGrid#toggleMatch.
 */
export const MATCH_ROLE: BoardHoldRole = 'match';

/**
 * How many holds a climb may give a role. You top out on two hands, so more
 * than a pair of finishes describes no climb anyone could set.
 *
 * A start is looser: two hands, but also the feet it is started off, and a sit
 * start marks all four. Past that it stops being a start position and becomes
 * the first half of the climb. The roles left out are unbounded.
 *
 * Kept in sync with BoardClimb::HOLD_ROLE_LIMITS, which refuses the rest on
 * save. A climb drawn on a photo has no server-side cap, but is held to the same
 * numbers so a hold means the same thing on either surface.
 */
export const HOLD_ROLE_LIMITS: Partial<Record<BoardHoldRole, number>> = { start: 4, finish: 2 };

/**
 * The roles a problem cannot do without: you pull on off a start, you move on
 * holds, and you top out. A selection missing any of the three is not a problem
 * anyone could climb yet, so it cannot be saved — the two roles left out (feet
 * and no-match) only qualify the ones in between.
 */
export const REQUIRED_HOLD_ROLES: BoardHoldRole[] = ['start', 'hand', 'finish'];

/** Which of the required roles are still unplaced, in legend order. */
export function missingHoldRoles(holds: BoardHold[]): BoardHoldRole[] {
  return REQUIRED_HOLD_ROLES.filter((role) => !holds.some((hold) => hold.role === role));
}

/** Whether this role has all the holds it is allowed, so it can take no more. */
export function roleAtLimit(holds: BoardHold[], role: BoardHoldRole): boolean {
  const limit = HOLD_ROLE_LIMITS[role];
  if (limit === undefined) return false;

  return holds.filter((hold) => hold.role === role).length >= limit;
}

/** The role a board hold carries, which is what the circuit rules read. */
export const boardHoldRole = (hold: BoardHold): BoardHoldRole => hold.role;

/**
 * Start holds first, finish holds last, everything else exactly where the setter
 * put it. The board's name for the shared rule — see circuit.ts, which is where
 * every surface's ordering, numbering and handing now lives.
 */
export function orderHolds(holds: BoardHold[]): BoardHold[] {
  return orderCircuit(holds, boardHoldRole);
}

/**
 * A hold the climber stands on rather than moves to. It belongs to the problem
 * like any other, but it is not a step of the circuit: the run never stops on
 * one, it wears no number, and it is lit for the whole of the run rather than
 * as it is reached — the wall does the same, see BoardLightPayload#blink_frame.
 */
export function isFootHold(hold: BoardHold): boolean {
  return hold.role === 'foot';
}

/** The run itself, as indexes into the selection: every entry but the feet. */
export function stepIndexes(holds: BoardHold[]): number[] {
  return circuitSteps(holds, boardHoldRole);
}

/** The other half of the selection: the feet, in the same terms. */
export function footIndexes(holds: BoardHold[]): number[] {
  return circuitFeet(holds, boardHoldRole);
}

/**
 * One entry per cell the circuit uses, however many times it uses it.
 *
 * A circuit may come back to a hold — the same one can be its 5th and its 20th
 * — but the wall has one LED there and the photo has one circle, so everything
 * drawn per cell has to decide what that one circle says. It carries every
 * number the cell takes ("5,20") and is drawn as one of its uses, whichever
 * ranks first — see drawRank.
 *
 * The same rule the wall lights by — see BoardLightPayload#use_rank, which adds
 * the one use this cannot know about: the hold a run is heading for.
 */
export interface CellUse {
  /** The use the cell is drawn as: its role, its colour, the hand on it. */
  hold: BoardHold;
  /**
   * Every place the cell takes in the run, in order: "5" or "5,20". Empty when
   * the cell is only ever stood on: a foot is not a step, so it has no place in
   * the run to wear.
   */
  label: string;
  /** Where each of those uses sits in the holds array. */
  indexes: number[];
  /**
   * The problem stands on this cell at some point. Those are shown for the
   * whole of the run rather than dimmed down while it points elsewhere — the
   * climber's foot stays there, and the wall keeps the LED on for that reason
   * (see BoardLightPayload#blink_frame). True even where the cell is drawn as
   * something else: a hold both stood on and pulled on is still a foot.
   */
  standing: boolean;
}

export function cellUses(holds: BoardHold[]): Map<string, CellUse> {
  const uses = new Map<string, CellUse>();
  // The numbers the run gives each hold: none to a foot, which it passes over,
  // and two to a match, which is one hold the climber makes two moves on.
  const numbers = circuitNumbers(holds, boardHoldRole);

  holds.forEach((hold, index) => {
    const foot = isFootHold(hold);
    const label = numbers[index] ?? '';
    const seen = uses.get(holdKey(hold));

    if (!seen) {
      uses.set(holdKey(hold), { hold, label, indexes: [index], standing: foot });
      return;
    }

    seen.label = [seen.label, label].filter(Boolean).join(',');
    seen.indexes.push(index);
    seen.standing ||= foot;
    if (drawRank(hold) < drawRank(seen.hold)) seen.hold = hold;
  });

  return uses;
}

/**
 * Which use of a cell the one circle is drawn as, lowest first.
 *
 * The two ends of the problem win it: green and red are where the problem
 * starts and where it is over, and a hold climbed through on the way must not
 * take that away. A foot loses to everything: a hold the circuit both stands on
 * and pulls on is a hand hold that happens to take a foot too, so it is drawn
 * — and lit — as the hand. Between equals the use the climber meets first wins,
 * which is what the ties in this comparison leave standing.
 */
function drawRank(hold: BoardHold): number {
  if (isEnd(hold.role)) return 0;

  return isFootHold(hold) ? 2 : 1;
}

function isEnd(role: BoardHoldRole): boolean {
  return role === 'start' || role === 'finish';
}

/** Colour per hold role. Kept in sync with BoardLightPayload::ROLE_COLORS. */
export const ROLE_COLORS: Record<BoardHoldRole, string> = {
  start: '#22c55e', // green
  hand: '#3b82f6', // blue
  foot: '#f97316', // orange
  no_match: '#fde047', // light yellow
  // Pink, and pink alone was free: the wall's own "go here next" takes magenta,
  // fuchsia, cyan or white (CIRCUIT_HIGHLIGHT_COLORS), and purple is the right
  // hand on a forced circuit. A match lit in any of those would read as one of
  // them.
  match: '#ec4899', // pink
  finish: '#ef4444', // red
};

/**
 * Colour per hand, on a circuit that forces them: the wall lights its holds by
 * the hand that takes them rather than by their role, and a board's grid draws
 * them the same way (see `boardRingColor`). Kept in sync with
 * BoardLightPayload::HAND_COLORS.
 *
 * The two ends keep their own colour on the wall — green and red are where the
 * problem starts and where it is over, which forcing hands does not change
 * (BoardLightPayload#color_override).
 */
export const HAND_COLORS: Record<BoardHand, string> = {
  left: '#3b82f6', // blue — the colour a hand hold has always had
  right: '#a855f7', // purple
};

/**
 * The mark every picture of a forced circuit puts on a hold to say which hand
 * takes it: an arc of the ring, on the hand's side, in this colour. Orange —
 * the one colour the setter asked for, and the one that reads against every
 * ring a hold can wear (blue, purple, pink, red). It is also a foot's colour,
 * but a foot is a whole orange ring with no number, and this is a stroke of one
 * on a ring of another colour.
 */
export const HAND_MARK_COLOR = '#f97316';

/** How much of the ring the mark takes, in degrees, centred on the hand's side. */
export const HAND_MARK_DEGREES = 45;

/**
 * The hands a hold is taken with, on a forced circuit: none on a start, a foot or
 * an unforced problem; one on an ordinary hold; both on a match, the one that
 * arrives first. The board's name for the shared rule — see circuit.ts.
 */
export function holdHands(hold: BoardHold): BoardHand[] {
  return circuitHandsOf(hold, boardHoldRole);
}

/**
 * The colour a board hold's ring — and its fill — is drawn in: its role's, or,
 * on a forced circuit, the one hand that takes it, which is what the wall lights
 * it in (BoardLightPayload#color_override). A grid hold is a few percent of the
 * wall wide and wears a number, so the ring is most of what can be seen of it;
 * the mark on its side (`handMarkGradient`) says *which* hand, the colour says
 * it from across the room. A photo's circles are big enough for the mark alone.
 *
 * The two ends keep green and red, as on the wall: where the problem starts and
 * where it is over is not changed by forcing hands. A match keeps its pink,
 * unlike on the wall, which has one colour per LED — both hands are on it, and
 * its two marks say so.
 */
export function boardRingColor(hold: BoardHold): string {
  if (hold.role === 'start' || hold.role === 'finish') return ROLE_COLORS[hold.role];

  const hands = holdHands(hold);
  return hands.length === 1 ? HAND_COLORS[hands[0]] : ROLE_COLORS[hold.role];
}

/**
 * Where on the ring each hand's mark sits: the arc's two ends, in degrees
 * clockwise from the top — the way a CSS conic gradient counts, and what the
 * SVG picture turns into points on its ellipse. The left hand's mark is centred
 * on the left of the ring (270°), the right hand's on the right (90°): the
 * climber reads it the way they look at the wall.
 */
export function handMarkArc(hand: BoardHand): { from: number; to: number } {
  const centre = hand === 'left' ? 270 : 90;

  return { from: centre - HAND_MARK_DEGREES / 2, to: centre + HAND_MARK_DEGREES / 2 };
}

/**
 * The marks as a CSS conic gradient over the whole disc — the mark's colour
 * over each named hand's arc, transparent elsewhere — for a ring-shaped mask to
 * cut down to the ring (see `HandMark`). Undefined where no hand is named, so a
 * hold that names none draws nothing extra.
 */
export function handMarkGradient(hands: BoardHand[]): string | undefined {
  if (hands.length === 0) return undefined;

  const arcs = [...new Set(hands)].map(handMarkArc).sort((a, b) => a.from - b.from);
  const stops: string[] = [];
  let at = 0;
  for (const arc of arcs) {
    stops.push(`transparent ${at}deg ${arc.from}deg`, `${HAND_MARK_COLOR} ${arc.from}deg ${arc.to}deg`);
    at = arc.to;
  }
  stops.push(`transparent ${at}deg 360deg`);

  return `conic-gradient(${stops.join(', ')})`;
}

/**
 * Which hand takes each hold of a circuit that forces them, given the hand it
 * starts on. The twin of BoardClimb#assign_hands, which is what the server
 * actually stores — this is here so the setter sees the alternation land as
 * they paint, and so the live preview lights the wall with it.
 *
 * The hands alternate down the sequence. Feet are skipped rather than counted:
 * a foot chip is not a hand, and counting it would flip the alternation for a
 * hold no hand ever touches. A hold both hands land on needs nothing of its
 * own: the circuit uses it twice, so the alternation gives it one hand and then
 * the other by itself.
 */
export function assignHands(holds: BoardHold[], startingHand: BoardHand): BoardHold[] {
  return assignCircuitHands(holds, boardHoldRole, startingHand);
}

/** Strips the hands off a selection, for a problem that stops forcing them. */
export function clearHands(holds: BoardHold[]): BoardHold[] {
  return clearCircuitHands(holds);
}

/**
 * Swap the hand this hold is taken with, and let the run alternate on from it
 * — see `circuit.ts#swapCircuitHand`.
 */
export function swapHand(holds: BoardHold[], index: number): BoardHold[] {
  return swapCircuitHand(holds, boardHoldRole, index);
}

export const otherHand = otherCircuitHand;

/**
 * The hand a circuit starts on, read back off its holds — the first one that
 * names a hand is the first of the sequence, every save having rewritten the
 * run from it. Falls back to the left hand, as the server does.
 */
export function startingHandOf(holds: BoardHold[]): BoardHand {
  return circuitStartingHand(holds, boardHoldRole);
}

/**
 * What a circuit's current hold is lit in, instead of its role colour — none of
 * these is a role colour, so it reads as "go here" rather than as another hold.
 * Kept in sync with Board::CIRCUIT_HIGHLIGHT_COLORS.
 */
export const CIRCUIT_HIGHLIGHT_COLORS: Record<CircuitHighlightColor, string> = {
  magenta: '#ff00ff',
  fuchsia: '#d946ef',
  cyan: '#22d3ee',
  white: '#ffffff',
};

/** Legend/picker order, and what an unconfigured board is lit in. */
export const CIRCUIT_HIGHLIGHT_CYCLE: CircuitHighlightColor[] = [
  'magenta',
  'fuchsia',
  'cyan',
  'white',
];

/**
 * The colour this board's wall paints a circuit's current hold, so the photo can
 * show the same one — or null when the board picks its own: a Moonboard is sent
 * the protocol's letters and colours them itself, so there the hold only flashes.
 */
export function circuitHighlightColor(board: Board | null | undefined): string | null {
  if (!board || board.board_type.light_protocol === 'moonboard') return null;

  return CIRCUIT_HIGHLIGHT_COLORS[board.resolved_circuit_highlight_color];
}

/**
 * Icon per hold role. The legend buttons are icon-only until the setter asks
 * for the names, so each icon has to carry the role on its own: a play triangle
 * for where the problem begins, a hand, footprints, a broken link for the holds
 * that cannot be matched on, and a flag for the top.
 */
export const ROLE_ICONS: Record<BoardHoldRole, LucideIcon> = {
  start: Play,
  hand: Hand,
  foot: Footprints,
  no_match: Unlink,
  // The broken link's opposite: a match is the two hands joined on one hold.
  match: Link,
  finish: Flag,
};

/**
 * Black or white, whichever reads on a role's colour — the selected legend
 * button is filled with it, and light yellow swallows white.
 */
export function roleForeground(color: string): string {
  const [red, green, blue] = [1, 3, 5].map((index) => parseInt(color.slice(index, index + 2), 16) / 255);

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue > 0.7 ? '#000000' : '#ffffff';
}
